import { useTree } from '@mantine/core';
import { useEffect, useMemo, useRef } from 'react';
import { getAncestors, type SpecNode, type SpecTree } from './spec-tree';

/**
 * Tree state for browsing a spec tree alongside a details column. Keeps the selected node in view, and lets the
 * details column navigate to another node by expanding everything above it.
 */
export function useSpecTreeNavigation(specTree: SpecTree, initiallyExpanded: (node: SpecNode) => boolean) {
  const initialExpandedState = useMemo(
    () => Object.fromEntries([...specTree.nodes.values()].filter(initiallyExpanded).map((node) => [node.value, true])),
    [specTree, initiallyExpanded],
  );
  const tree = useTree({ initialExpandedState });
  const [selectedValue] = tree.selectedState;
  const selectedNode = selectedValue ? specTree.nodes.get(selectedValue) : undefined;
  const treeViewport = useRef<HTMLDivElement>(null);
  const detailsViewport = useRef<HTMLDivElement>(null);

  // The tree keeps the selected node in view itself, while the details start from the top for each node
  useEffect(() => {
    if (selectedValue) detailsViewport.current?.scrollTo({ top: 0 });
  }, [selectedValue]);

  const navigateTo = (value: string) => {
    tree.setExpandedState((current) => {
      const next = { ...current };
      getAncestors(specTree.nodes, value).forEach((ancestor) => {
        next[ancestor.value] = true;
      });
      return next;
    });
    tree.select(value);
  };

  return { tree, selectedNode, treeViewport, detailsViewport, navigateTo };
}
